import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardBody,
  CardTitle
} from "reactstrap";
import { withTranslation } from "react-i18next";

import { Link, useParams, useLocation } from 'react-router-dom';

import { motion } from "framer-motion";
import { slideUp } from './slideUp';

import axiosInstance from "utils/axiosInstance";
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";

import home4 from "assets/images/info/home4.png";
import placeholder from "assets/images/info/card-placeholder-medium.png";

import "./CollectionList.css";
import "assets/css/remixicon.css";

import {
  saveThumbnailToIndexedDB,
  getThumbnailFromIndexedDB
} from "utils/thumbnails/indexedDBThumbnails";
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";

/*----------------------
/* Test Data
import projectImg_2 from "../../../assets/images/contract-type/contract-type-2.jpg";
import projectImg_5 from "../../../assets/images/contract-type/contract-type-5.jpg";
import projectImg_7 from "../../../assets/images/contract-type/contract-type-7.jpg";
import projectImg_9 from "../../../assets/images/contract-type/contract-type-9.jpg";
import projectImg_10 from "../../../assets/images/contract-type/contract-type-10.jpg";
const projectData = [
  { id: 1, project_name: "勞動契約", details: "適用於雇主僱用勞工（員工）。", src: projectImg_2 },
  { id: 2, project_name: "房屋租賃契約", details: "適用於出租、承租房屋。", src: projectImg_5 },
  { id: 3, project_name: "合夥契約", details: "適用於合夥人出資共同成立「合夥事業」", src: projectImg_7 },
]
/*----------------------*/

const chunkArray = (arr, size) => {
  const chunked = [];
  for (let i = 0; i < arr.length; i += size) {
    chunked.push(arr.slice(i, i + size));
  }
  return chunked;
};


const CollectionList = ({ t }) => {

  const { themeId } = useParams();
  const location = useLocation();
  const { themeName } = location.state || {};

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [myInfoTheme, setMyInfoTheme] = useState([]);
  const [myInfoCollections, setMyInfoCollections] = useState([]);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [thumbnailMediumUrls, setThumbnailMediumUrls] = useState({});

  const [themeLoading, setThemeLoading] = useState(true);
  const [dataFetchError, setDataFetchError] = useState(null);


  //--------------------------------------------------
  // On mount, load theme and collections
  //--------------------------------------------------
  const fetchThemeAndCollections = async () => {
    try {
      setThemeLoading(true);
      setDataFetchError(null);

      // Fetch both in parallel
      const [themeResponse, collectionResponse] = await Promise.all([
        axiosInstance.get(`/knovia/myInfo/${themeId}/getThemeInfo`),
        axiosInstance.get(`/knovia/myInfo/${themeId}/getCollectionsInTheme`)
      ]);

      // Set theme
      setMyInfoTheme(themeResponse.data);

      // Sort collections by order
      const sortedCollections = [...collectionResponse.data].sort((a, b) => a.order - b.order);
      setMyInfoCollections(sortedCollections);

      return sortedCollections;
    } catch (err) {
      console.error("Theme or collections fetch error:", err);
      setDataFetchError("Failed to load theme or collections.");
      return [];
    } finally {
      setThemeLoading(false);
    }
  };

  const fetchThumbnails = async (collectionIds) => {
    try {
      await fetchThumbnailsWithCache(
        collectionIds, 
        'infoCollection', 
        'sizeMedium',
        ({ id, url }) => {
          setThumbnailMediumUrls(prev => ({
            ...prev,
            [id]: url
          }));
        }
      );
      // Fetch original thumbnails one by one
      for (const entry of collectionIds) {
        await fetchThumbnailsWithCache(
          [entry], // Pass a single item array
          'infoCollection',
          'sizeOriginal',
          ({ id, url }) => {
            setThumbnailUrls((prev) => ({
              ...prev,
              [id]: url,
            }));
          }
        );
      }
    } catch (err) {
      console.error("Unexpected error in fetchThumbnails:", err);
    }
  };

  useEffect(() => {
    (async () => {
      const sortedCollections = await fetchThemeAndCollections();
      if (sortedCollections.length > 0) {
        fetchThumbnails(sortedCollections); // non-blocking phase
      }
    })();
  }, [themeId]);



  //--------------------------------------------------
  // Active Collection Toggles
  //--------------------------------------------------

  const [activeIndex, setActiveIndex] = useState(5);

  useEffect(() => {
    const slideCount = document.querySelectorAll('.main-box li').length - 1;
    const slideWidth = 100 / slideCount + '%';
    document.querySelectorAll('.box').forEach((element) => element.style.width = slideWidth)
  }, []);
 
  const handleMouseEnter = (index) => {
    setActiveIndex(index);
  };



  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (

    <div className="page-content collection-list"
      style={{ 
        backgroundImage: `url(${home4})`,
      }}
    >

      <Container fluid>
        <BreadcrumbAboveImage
          title={t("My Info")}
          breadcrumbItems={[
            { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" },
            { title: t("選擇主題類別"), to: "/myInfo/navigateThemes" },
            { title: t(`${myInfoTheme?.label || themeName}`) }
          ]}
        />

        <Card className="collection-list-card">
          <CardBody className="collection-list-cardbody" style={{ minHeight: "900px" }}>

            <div className="projects-area pb-75">
              <div className="container">

                {/* Title */}
                <div className="projects-section-title">
                  <motion.div className="row align-items-center"
                    initial="offscreen"
                    whileInView="onscreen"
                    variants={slideUp()}
                    viewport={{ once: true, amount: 0.4 }}
                  >
                    <div className="col-lg-7 col-md-8">
                      <div className="title">
                        <span className="d-block glow-button" 
                          style={{ fontWeight:'400', width: 'fit-content', padding: '0px 2px' }}
                        >
                          {myInfoTheme?.description}
                        </span>
                        <h2>{myInfoTheme?.label}</h2>
                      </div>
                    </div>
                    <div className="col-lg-5 col-md-4">
                      <div className="button text-end">
                        <Link className="demo text-decoration-none" to="../myInfo/navigateThemes">
                          返回類別總表
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>


                {/* Collection Boxes */}
                <motion.div className="projects-box-info"
                  initial="offscreen"
                  whileInView="onscreen"
                  variants={slideUp()}
                  viewport={{ once: true, amount: 0.4 }}
                >
                  {
                    [...Array(Math.ceil(myInfoCollections.length / 5))].map((_, rowIndex) => {
                      const group = myInfoCollections.slice(rowIndex * 5, rowIndex * 5 + 5);
                      const placeholdersNeeded = 5 - group.length;
                      const globalStartIndex = rowIndex * 5;

                      return (
                        <ul className="main-box" key={rowIndex}>
                          {
                            group.map(({ _id, description, label, src }, i) => {
                              const globalIndex = rowIndex * 5 + i;
                              return (
                                <li 
                                  key={globalIndex}
                                  className={`box ${activeIndex === globalIndex ? 'active' : ''}`} 
                                  onMouseEnter={() => handleMouseEnter(globalIndex)}
                                  style={{ 
                                    /*backgroundImage: `url("/images/contract-type/contract-type-${globalIndex + 1}.jpg")` */
                                    backgroundImage: `url(${thumbnailUrls[_id] || thumbnailMediumUrls[_id] || placeholder})`
                                  }}
                                >
                                  <span>
                                    {/*
                                    <img 
                                      src={thumbnailUrls[id] || placeholder} 
                                      className="projects-image"
                                      alt="projects-image" 
                                      style={{ objectFit: 'cover', objectPosition: '10% 50%' }}
                                    />
                                    */}
                                  </span>
                                  <div className="detail">
                                    <div className="project-content">
                                      <h3>
                                        <Link className="text-decoration-none" to="/service-details">
                                          {label}
                                        </Link>
                                      </h3>
                                      <p>{description}</p>
                                      <Link className="read-more text-decoration-none" 
                                        to={`../myInfo/collections/${_id}`}
                                        state={{ themeName: myInfoTheme?.label, collectionName: label }}
                                      >
                                        選擇此主題集
                                        <i className="ri-arrow-right-line"></i>
                                      </Link>
                                    </div>
                                  </div>
                                </li>
                              );
                            })
                          }

                          {/* Pad with placeholders to keep layout consistent */}
                          {
                            Array.from({ length: placeholdersNeeded }).map((_, idx) => (
                              <li key={`placeholder-${idx}`} className="box placeholder"></li>
                            ))
                          }
                          
                        </ul>
                      );
                    })
                  }
                </motion.div>

              </div>
            </div>

          </CardBody>
        </Card>

      </Container>
    </div>
  );
};

export default withTranslation()(CollectionList);


